import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { DocProposal, DocProposalHunk } from "@/lib/api";

type Props = {
  proposal: DocProposal;
  disabled?: boolean;
  pending?: boolean;
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onAcceptSelected: (ids: string[]) => void;
  onRejectSelected: (ids: string[]) => void;
};

export default function DocProposalPreview({
  proposal,
  disabled,
  pending,
  onAcceptAll,
  onRejectAll,
  onAcceptSelected,
  onRejectSelected,
}: Props) {
  const hunks: DocProposalHunk[] = proposal.hunks || [];
  const key = hunks.map((h) => h.id).join("|");
  const [picked, setPicked] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const next: Record<string, boolean> = {};
    for (const h of hunks) next[h.id] = true;
    setPicked(next);
  }, [key]);

  const selectedIds = hunks.filter((h) => picked[h.id]).map((h) => h.id);
  const busy = Boolean(disabled || pending);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-amber-200 bg-white">
      <div className="flex flex-wrap items-center gap-2 border-b border-amber-200 bg-amber-50 px-3 py-2">
        <p className="mr-auto text-xs font-medium text-amber-950">
          Propozycja AI - zatwierdź lub odrzuć
          {proposal.summary ? (
            <span className="ml-1 font-normal text-amber-800/80">({proposal.summary})</span>
          ) : null}
        </p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8"
          disabled={busy}
          onClick={onRejectAll}
        >
          Odrzuć wszystko
        </Button>
        <Button type="button" size="sm" className="h-8" disabled={busy} onClick={onAcceptAll}>
          Zatwierdź wszystko
        </Button>
      </div>

      <div
        className="min-h-[200px] flex-1 overflow-y-auto border-b border-slate-100"
        dangerouslySetInnerHTML={{
          __html: proposal.preview_html || "<p class=\"p-3 text-sm text-slate-500\">Brak podglądu</p>",
        }}
      />

      <div className="max-h-48 space-y-2 overflow-y-auto border-t border-slate-100 bg-slate-50/80 p-3">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          Hunksy ({hunks.length})
        </p>
        {hunks.map((h) => (
          <label
            key={h.id}
            className={cn(
              "flex cursor-pointer gap-2 rounded-md border bg-white p-2 text-xs",
              picked[h.id] ? "border-emerald-200" : "border-slate-200 opacity-70",
            )}
          >
            <input
              type="checkbox"
              className="mt-0.5"
              checked={Boolean(picked[h.id])}
              disabled={busy}
              onChange={(e) =>
                setPicked((prev) => ({ ...prev, [h.id]: e.target.checked }))
              }
            />
            <span className="min-w-0 flex-1 space-y-1">
              <span className="block font-medium text-slate-700">
                {h.id}
                {h.ambiguous ? (
                  <span className="ml-1 text-[10px] font-normal text-amber-700">
                    (fragment niejednoznaczny)
                  </span>
                ) : null}
              </span>
              {h.note ? <span className="block text-slate-500">{h.note}</span> : null}
              <span className="block whitespace-pre-wrap text-red-800/90 line-through decoration-red-700/40">
                {h.before.slice(0, 280)}
                {h.before.length > 280 ? "…" : ""}
              </span>
              <span className="block whitespace-pre-wrap text-emerald-900">
                {h.after.slice(0, 280)}
                {h.after.length > 280 ? "…" : ""}
              </span>
            </span>
          </label>
        ))}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8"
            disabled={busy || selectedIds.length === 0}
            onClick={() => onRejectSelected(selectedIds)}
          >
            Odrzuć zaznaczone
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-8"
            disabled={busy || selectedIds.length === 0}
            onClick={() => onAcceptSelected(selectedIds)}
          >
            Wdróż zaznaczone
          </Button>
        </div>
      </div>
    </div>
  );
}
